import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, CheckCircle, Shield, Lock, Crown, Sparkles, 
  ChevronLeft, ChevronRight, Star, Briefcase, Code2, GraduationCap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample template previews (static for landing page)
const SAMPLE_TEMPLATES = [
  { id: 'corporate', name: 'Corporate Classic', category: 'Professional', isPremium: false },
  { id: 'modern_sidebar', name: 'Modern Sidebar', category: 'Modern', isPremium: false },
  { id: 'gradient_header', name: 'Gradient Header', category: 'Modern', isPremium: true },
  { id: 'tech_dark', name: 'Tech Dark', category: 'Technical', isPremium: true },
  { id: 'executive_premium', name: 'Executive Premium', category: 'Executive', isPremium: false },
  { id: 'minimalist_pure', name: 'Minimalist Pure', category: 'Creative', isPremium: true },
];

export const LoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [templates, setTemplates] = useState(SAMPLE_TEMPLATES);
  const [templatePreviews, setTemplatePreviews] = useState({});

  // Fetch actual templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${API}/resumes/templates`);
        if (response.ok) {
          const data = await response.json();
          // Take first 6 templates for preview
          const previewTemplates = data.templates?.slice(0, 6) || SAMPLE_TEMPLATES;
          setTemplates(previewTemplates);
          
          // Fetch previews for each template
          previewTemplates.forEach(async (template) => {
            try {
              const previewRes = await fetch(`${API}/resumes/templates/${template.id}/preview`);
              if (previewRes.ok) {
                const html = await previewRes.text();
                setTemplatePreviews(prev => ({ ...prev, [template.id]: html }));
              }
            } catch (e) {
              console.log('Preview fetch error:', e);
            }
          });
        }
      } catch (error) {
        console.log('Using sample templates');
      }
    };
    fetchTemplates();
  }, []);

  // Auto-rotate templates
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTemplateIndex((prev) => (prev + 1) % templates.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [templates.length]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      // Check for redirect URL stored before login (check both storages)
      let redirectUrl = sessionStorage.getItem('redirectAfterLogin') || localStorage.getItem('redirectAfterLogin');
      
      if (redirectUrl) {
        // Clear from both storages
        sessionStorage.removeItem('redirectAfterLogin');
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, navigate]);

  // Don't render if user is logged in (will redirect)
  if (user) {
    return null;
  }

  const handleGoogleLogin = () => {
    // Direct Google OAuth - redirects to our backend which then redirects to Google
    window.location.href = `${API}/auth/google/login`;
  };

  const nextTemplate = () => {
    setCurrentTemplateIndex((prev) => (prev + 1) % templates.length);
  };

  const prevTemplate = () => {
    setCurrentTemplateIndex((prev) => (prev - 1 + templates.length) % templates.length);
  };

  const currentTemplate = templates[currentTemplateIndex];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex flex-col">
      {/* Hero Section */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
          {/* Left: Branding & Auth */}
          <div className="space-y-6">
            {/* Logo */}
            <div className="inline-flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-soft">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <span className="font-heading font-bold text-2xl text-primary">
                FixResume24
              </span>
            </div>
            
            {/* Headline */}
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-primary tracking-tight leading-tight">
              Professional refinement.
              <br />
              <span className="text-secondary">29+ Templates.</span>
            </h1>
            
            <p className="text-base text-muted-foreground max-w-md">
              Enhance your resume's clarity and impact with AI-powered refinement 
              and choose from 29+ professional templates.
            </p>

            {/* Features */}
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full">
                <CheckCircle className="h-4 w-4" />
                <span>AI Grammar Fix</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full">
                <Sparkles className="h-4 w-4" />
                <span>29+ Templates</span>
              </div>
              <div className="flex items-center gap-2 bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full">
                <Shield className="h-4 w-4" />
                <span>Privacy First</span>
              </div>
            </div>

            {/* Auth Card */}
            <Card className="shadow-sm border-slate-200" data-testid="auth-card">
              <CardContent className="p-5 space-y-4">
                <Button 
                  variant="outline" 
                  className="w-full h-12 text-base font-medium border-slate-200 hover:bg-slate-50 hover:border-primary/50 transition-all"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                >
                  <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  By continuing, you agree to our{' '}
                  <Link to="/terms" className="text-primary hover:underline font-medium">Terms</Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline font-medium">Privacy Policy</Link>
                </p>

                <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center pt-2 border-t">
                  <Lock className="h-3 w-3" />
                  <span>Resume data is never stored permanently</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Template Preview Carousel */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Template Preview Card */}
              <Card className="overflow-hidden shadow-lg border-slate-200">
                <CardHeader className="p-4 bg-slate-50 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold">{currentTemplate?.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {currentTemplate?.category}
                        </Badge>
                        {currentTemplate?.isPremium && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            <Crown className="h-3 w-3 mr-1" />
                            Premium
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={prevTemplate}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={nextTemplate}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Template Preview */}
                  <div className="h-[400px] overflow-hidden bg-white">
                    {templatePreviews[currentTemplate?.id] ? (
                      <div 
                        className="transform scale-[0.45] origin-top-left w-[222%] h-[222%] pointer-events-none"
                        dangerouslySetInnerHTML={{ __html: templatePreviews[currentTemplate?.id] }}
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
                          <p className="text-sm">Template Preview</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Template Indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {templates.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTemplateIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentTemplateIndex 
                        ? 'bg-primary w-6' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>

              {/* Template Count Badge */}
              <div className="absolute -top-3 -right-3 bg-primary text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                29+ Templates
              </div>
            </div>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-2 mt-6 justify-center">
              {['Professional', 'Modern', 'Creative', 'Technical', 'Executive'].map((cat) => (
                <span 
                  key={cat}
                  className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium"
                >
                  {cat}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 border-t border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center space-y-2">
          <p className="text-xs text-muted-foreground">
            AI-powered resume refinement. Users are responsible for accuracy of content.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/terms" className="text-xs text-primary hover:underline">Terms & Conditions</Link>
            <span className="text-xs text-muted-foreground">|</span>
            <Link to="/privacy-policy" className="text-xs text-primary hover:underline">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
